import * as React from 'react';
import styles from './index.less';
import FDrawer from '@/components/FDrawer';
import { Space } from 'antd';
import FComponentsLib from '@freelog/components-lib';
import { FI18n, FUtil } from '@freelog/tools-lib';
// import FSingleLineInput from '@/components/FSingleLineInput';
import FInput_PinyinSafeTextCounter from '@/components/FInput_PinyinSafeTextCounter';

interface FResourcePropertyEditorDrawerProps {
  disabledKeys: string[];
  disabledNames: string[];
  defaultData: {
    key: string;
    name: string;
    description: string;
  } | null;
  noneEditableFields?: Array<'key' | 'name' | 'description'>;

  onOk?(data: { key: string; name: string; description: string }): void;

  onClose?(): void;
}

interface FResourcePropertyEditorDrawerStates {
  visible: boolean;
  nameInput: string;
  nameInputError: string;
  keyInput: string;
  keyInputError: string;
  descriptionInput: string;
  descriptionInputError: string;
}

const initStates: FResourcePropertyEditorDrawerStates = {
  visible: true,
  nameInput: '',
  nameInputError: '',
  keyInput: '',
  keyInputError: '',
  descriptionInput: '',
  descriptionInputError: '',
};

function FResourcePropertyEditorDrawer({
  disabledKeys,
  disabledNames,
  defaultData,
  noneEditableFields = [],
  onOk,
  onClose,
}: FResourcePropertyEditorDrawerProps) {
  const [$visible, set$visible, get$visible] = FUtil.Hook.useGetState<
    FResourcePropertyEditorDrawerStates['visible']
  >(initStates['visible']);
  const [$nameInput, set$nameInput, get$nameInput] = FUtil.Hook.useGetState<
    FResourcePropertyEditorDrawerStates['nameInput']
  >(initStates['nameInput']);
  const [$nameInputError, set$nameInputError, get$nameInputError] = FUtil.Hook.useGetState<
    FResourcePropertyEditorDrawerStates['nameInputError']
  >(initStates['nameInputError']);
  const [$keyInput, set$keyInput, get$keyInput] = FUtil.Hook.useGetState<
    FResourcePropertyEditorDrawerStates['keyInput']
  >(initStates['keyInput']);
  const [$keyInputError, set$keyInputError, get$keyInputError] = FUtil.Hook.useGetState<
    FResourcePropertyEditorDrawerStates['keyInputError']
  >(initStates['keyInputError']);
  const [$descriptionInput, set$descriptionInput, get$descriptionInput] = FUtil.Hook.useGetState<
    FResourcePropertyEditorDrawerStates['descriptionInput']
  >(initStates['descriptionInput']);
  const [$descriptionInputError, set$descriptionInputError, get$descriptionInputError] =
    FUtil.Hook.useGetState<FResourcePropertyEditorDrawerStates['descriptionInputError']>(
      initStates['descriptionInputError'],
    );

  function initData() {
    if (defaultData) {
      set$nameInput(defaultData.name);
      set$keyInput(defaultData.key);
      set$descriptionInput(defaultData.description);
    } else {
      set$nameInput(initStates['nameInput']);
      set$keyInput(initStates['keyInput']);
      set$descriptionInput(initStates['descriptionInput']);
    }
    set$keyInputError(initStates['keyInputError']);
    set$nameInputError(initStates['nameInputError']);
    set$descriptionInputError(initStates['descriptionInputError']);
  }

  return (
    <FDrawer
      // title={defaultData ? '编辑基础属性' : '补充属性'}
      title={
        defaultData
          ? FI18n.i18nNext.tAuto('resourceinfo_edit_title')
          : FI18n.i18nNext.tAuto('resourceinfo_add_title')
      }
      onClose={() => {
        set$visible(false);
      }}
      open={$visible}
      width={580}
      afterOpenChange={(v) => {
        if (!v) {
          onClose && onClose();
        } else {
          initData();
        }
      }}
      topRight={
        <Space size={30}>
          <FComponentsLib.FTextBtn
            type='default'
            onClick={() => {
              set$visible(false);
            }}
          >
            {FI18n.i18nNext.tAuto('btn_cancel')}
          </FComponentsLib.FTextBtn>

          <FComponentsLib.FRectBtn
            type='primary'
            disabled={
              $nameInput === '' ||
              $nameInputError !== '' ||
              $keyInput === '' ||
              $keyInputError !== '' ||
              $descriptionInputError !== ''
            }
            onClick={async () => {
              onOk?.({
                key: $keyInput,
                name: $nameInput,
                description: $descriptionInput,
              });
              set$visible(false);
            }}
          >
            {FI18n.i18nNext.tAuto('btn_save')}
          </FComponentsLib.FRectBtn>
        </Space>
      }
    >
      <Space size={20} direction='vertical' style={{ width: '100%' }}>
        <div className={styles.input}>
          <div className={styles.title}>
            <FComponentsLib.FContentText
              style={{ fontSize: 12 }}
              type={'highlight'}
              text={FI18n.i18nNext.tAuto('resourceinfo_add_input_name')}
            />
          </div>
          <div style={{ height: 5 }} />
          {/* <FSingleLineInput */}
          <FInput_PinyinSafeTextCounter
            lengthLimit={-1}
            style={{ border: '1px solid #d4d4d4' }}
            placeholder={FI18n.i18nNext.t('resourceinfo_add_input_name_hint')}
            disabled={noneEditableFields.includes('name')}
            value={$nameInput}
            className={styles.input}
            onChangeValue={(value) => {
              let errorText: string = '';
              if (value === '') {
                errorText = '输入属性名称';
              } else if (value.length > 50) {
                // errorText = '不超过50个字符';
                errorText = FI18n.i18nNext.tAuto('alert_naming_convention_attribute_name');
              } else if (disabledNames.includes(value) && value !== defaultData?.name) {
                errorText = '名称不能重复';
              }
              set$nameInput(value);
              set$nameInputError(errorText);
            }}
            hasError={$nameInputError !== ''}
          />
          {$nameInputError !== '' && (
            <>
              <div style={{ height: 5 }} />
              <div className={styles.errorTip}>{$nameInputError}</div>
            </>
          )}
        </div>

        <div className={styles.input}>
          <div className={styles.title}>
            <FComponentsLib.FContentText
              style={{ fontSize: 12 }}
              type={'highlight'}
              text={FI18n.i18nNext.t('resourceinfo_add_input_key')}
            />
          </div>
          <div style={{ height: 5 }} />
          {/* <FSingleLineInput */}
          <FInput_PinyinSafeTextCounter
            lengthLimit={-1}
            style={{ border: '1px solid #d4d4d4' }}
            placeholder={FI18n.i18nNext.t('resourceinfo_add_input_key_hint')}
            value={$keyInput}
            disabled={noneEditableFields.includes('key')}
            className={styles.input}
            onChangeValue={(value) => {
              let errorText: string = '';
              if (value === '') {
                errorText = '输入键';
              } else if (value.length > 30) {
                errorText = '不超过30个字符';
              } else if (disabledKeys.includes(value) && value !== defaultData?.key) {
                errorText = FI18n.i18nNext.tAuto('alert_key_exist');
              } else if (!FUtil.Regexp.CUSTOM_KEY.test(value)) {
                errorText = FI18n.i18nNext.tAuto('alert_naming_convention_key');
              }
              set$keyInput(value);
              set$keyInputError(errorText);
            }}
            hasError={$keyInputError !== ''}
          />
          {$keyInputError !== '' && (
            <>
              <div style={{ height: 5 }} />
              <div className={styles.errorTip}>{$keyInputError}</div>
            </>
          )}
        </div>

        <div className={styles.input}>
          <div className={styles.title}>
            <FComponentsLib.FContentText
              style={{ fontSize: 12 }}
              type={'highlight'}
              text={FI18n.i18nNext.t('resourceinfo_add_input_desc')}
            />
            <FComponentsLib.FContentText
              style={{ fontSize: 12 }}
              type={'additional2'}
              text={FI18n.i18nNext.t('form_input_label_optional')}
            />
          </div>
          <div style={{ height: 5 }} />
          {/* <FSingleLineInput */}
          <FInput_PinyinSafeTextCounter
            lengthLimit={-1}
            style={{ border: '1px solid #d4d4d4' }}
            value={$descriptionInput}
            className={styles.input}
            disabled={noneEditableFields.includes('description')}
            onChangeValue={(value) => {
              let errorText: string = '';
              if (value.length > 50) {
                errorText = FI18n.i18nNext.tAuto('alert_key_remark_length');
              }
              set$descriptionInput(value);
              set$descriptionInputError(errorText);
            }}
            placeholder={FI18n.i18nNext.t('resourceinfo_add_input_desc_hint')}
            hasError={$descriptionInputError !== ''}
          />
          {$descriptionInputError !== '' && (
            <>
              <div style={{ height: 5 }} />
              <div className={styles.errorTip}>{$descriptionInputError}</div>
            </>
          )}
        </div>
      </Space>
    </FDrawer>
  );
}

export default FResourcePropertyEditorDrawer;
